import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { usePagination } from '@/hooks/use-pagination';
import { useSidebar } from '@/components/ui/sidebar';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type FixedPaginationProps = {
  currentPage: number;
  totalPages: number;
  paginationItemsToDisplay?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSize?: number;
  totalItems?: number;
};

export function FixedPagination({
  currentPage,
  totalPages,
  paginationItemsToDisplay = 5,
  onPageChange,
  onPageSizeChange,
  pageSize = 10,
  totalItems = 0,
}: FixedPaginationProps) {
  const { state: sidebarState } = useSidebar();
  const { pages, showLeftEllipsis, showRightEllipsis } = usePagination({
    currentPage,
    totalPages,
    paginationItemsToDisplay,
  });

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePageSizeChange = (value: string) => {
    const newPageSize = parseInt(value);
    onPageSizeChange(newPageSize);
  };

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Classes condicionais baseadas no estado da sidebar
  const sidebarClasses =
    sidebarState === 'expanded'
      ? 'left-64' // Sidebar expandida: 256px (16rem)
      : 'left-0'; // Sidebar colapsada: vai até o final da tela

  return (
    <div
      className={`fixed bottom-0 ${sidebarClasses} right-0 z-40 bg-background dark:bg-card border-t shadow-lg px-6 py-4 transition-all duration-200 ease-linear`}
    >
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Informações da página */}
        <p className="text-sm text-muted-foreground order-2 sm:order-1" aria-live="polite">
          {totalItems > 0 ? (
            <>
              Mostrando <span className="text-foreground font-medium">{startItem}</span> a{' '}
              <span className="text-foreground font-medium">{endItem}</span> de{' '}
              <span className="text-foreground font-medium">{totalItems}</span> resultados
            </>
          ) : (
            'Nenhum resultado encontrado'
          )}
        </p>

        {/* Paginação centralizada */}
        <div className="order-1 sm:order-2">
          <Pagination>
            <PaginationContent>
              {/* Botão página anterior */}
              <PaginationItem>
                <PaginationLink
                  className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                  onClick={() => handlePageClick(currentPage - 1)}
                  aria-label="Ir para página anterior"
                  aria-disabled={currentPage === 1 ? true : undefined}
                  role={currentPage === 1 ? 'link' : undefined}
                >
                  <ChevronLeftIcon size={16} aria-hidden="true" />
                </PaginationLink>
              </PaginationItem>

              {/* Reticências à esquerda (...) */}
              {showLeftEllipsis && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {/* Links das páginas */}
              {pages.map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => handlePageClick(page)}
                    isActive={page === currentPage}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}

              {/* Reticências à direita (...) */}
              {showRightEllipsis && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {/* Botão próxima página */}
              <PaginationItem>
                <PaginationLink
                  className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
                  onClick={() => handlePageClick(currentPage + 1)}
                  aria-label="Ir para próxima página"
                  aria-disabled={currentPage === totalPages ? true : undefined}
                  role={currentPage === totalPages ? 'link' : undefined}
                >
                  <ChevronRightIcon size={16} aria-hidden="true" />
                </PaginationLink>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>

        {/* Resultados por página */}
        <div className="order-3">
          <Select
            value={pageSize.toString()}
            onValueChange={handlePageSizeChange}
            aria-label="Resultados por página"
          >
            <SelectTrigger id="results-per-page" className="w-fit whitespace-nowrap min-w-[120px]">
              <SelectValue placeholder="Itens por página" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 / página</SelectItem>
              <SelectItem value="20">20 / página</SelectItem>
              <SelectItem value="50">50 / página</SelectItem>
              <SelectItem value="100">100 / página</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
